import React, { useEffect, useRef } from 'react';
import { Button } from 'antd';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { getSmartLabelClassName } from '../../../../../utils/labelWidthUtils';
import ParcelFilterStyle from '../../../../delivery/components/ParcelFilterStyle';
import DateFilterItem from '../../../../delivery/components/DateFilterItem';
import { DriverFilterOption } from '../../../../../utils/driverFilterOption';

export type ViewMode = 'driver' | 'area' | 'dsp' | 'whs';

/** 远程搜索防抖间隔，与 components/SearchSelect 默认值保持一致 */
const SEARCH_DEBOUNCE_MS = 300;

export interface TaskMonitoringFilters {
    taskDate: dayjs.Dayjs;
    selectedWarehouses?: string[];
    selectedCities: string[];
    selectedDsps: string[];
    selectedAreas: string[];
    /** 司机 accountId 字符串数组（不是姓名，姓名允许重名，见 utils/driverFilterOption） */
    selectedDrivers: string[];
    viewMode: ViewMode;
}

interface TaskMonitoringFilterProps {
    filters: TaskMonitoringFilters;
    onFiltersChange: (filters: TaskMonitoringFilters) => void;
    cities: string[];
    dsps: Array<{ id: number; name: string }>; // 从汇总表提取的DSP数据
    areas: Array<{ id: number; name: string }>; // 从汇总表提取的区域数据
    drivers: DriverFilterOption[];
    showResetButton?: boolean;
    /** DSP 远程搜索；传入时 DSP 下拉由后端按关键词返回候选 */
    onDspSearch?: (keyword: string) => void;
    /** 区域远程搜索；传入时区域下拉由后端按关键词返回候选 */
    onAreaSearch?: (keyword: string) => void;
    /** 司机远程搜索；传入时司机下拉由后端按关键词返回候选 */
    onDriverSearch?: (keyword: string) => void;
    dspOptionsLoading?: boolean;
    areaOptionsLoading?: boolean;
    driverOptionsLoading?: boolean;
    dspMode?: boolean; // 是否为DSP经理
}

const TaskMonitoringFilter: React.FC<TaskMonitoringFilterProps> = ({
    filters,
    onFiltersChange,
    cities,
    dsps,
    areas,
    drivers,
    showResetButton = true,
    onDspSearch,
    onAreaSearch,
    onDriverSearch,
    dspOptionsLoading,
    areaOptionsLoading,
    driverOptionsLoading,
    dspMode = false
}) => {
    const { t } = useTranslation();

    // 下拉改为远程搜索，逐字符请求会打满后端，故与 SearchSelect 一致做 300ms 防抖。
    // DSP 与区域/司机同屏渲染，按字段各自计时，避免相互取消掉待发请求。
    const searchDebounceTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
    useEffect(() => {
        const timers = searchDebounceTimers.current;
        return () => timers.forEach((timer) => clearTimeout(timer));
    }, []);
    const withSearchDebounce = (field: string, handler?: (keyword: string) => void) => {
        if (!handler) return undefined;
        return (keyword: string) => {
            const pending = searchDebounceTimers.current.get(field);
            if (pending) clearTimeout(pending);
            searchDebounceTimers.current.set(field, setTimeout(() => handler(keyword), SEARCH_DEBOUNCE_MS));
        };
    };

    // 只在下拉收起时把关键词清回空，下次点开是范围内的默认列表。
    // 选中项时不能清：antd 多选选中后会自行清空输入框文本但不回调 onSearch，
    // 此时若跟着把关键词也清掉，候选会立刻回落成默认前 50 条，
    // 用户没法在同一批搜索结果里连续勾选（输入 f 选中 F052 后就看不到 F054 了）。
    const resetSearchKeyword = (field: string, handler?: (keyword: string) => void) => {
        if (!handler) return;
        const pending = searchDebounceTimers.current.get(field);
        if (pending) clearTimeout(pending);
        handler('');
    };
    const withKeywordResetOnClose = (field: string, handler?: (keyword: string) => void) => {
        if (!handler) return undefined;
        return (open: boolean) => {
            if (!open) resetSearchKeyword(field, handler);
        };
    };

    const labelTaskDate = String(t('wms.delivery.dailyTaskMonitoring.buttons.taskDate'));
    const labelDsp = String(t('wms.delivery.dailyTaskMonitoring.buttons.dsp'));
    const labelArea = String(t('wms.delivery.dailyTaskMonitoring.buttons.area'));
    const labelDriver = String(t('wms.delivery.dailyTaskMonitoring.buttons.driver'));

    const shouldShowDimensionFilters = true; // 所有角色都显示司机维度和区域维度的筛选

    const col1Labels = [labelTaskDate];
    const col2Labels = [labelDsp];
    const col3Labels = [labelArea, labelDriver];
    
    // 计算最近30天的日期范围
    const today = dayjs();
    const thirtyDaysAgo = today.subtract(29, 'day');
    
    // 禁用不在最近30天范围内的日期
    const disabledDate = (current: dayjs.Dayjs | null) => {
        if (!current) return false;
        return current.isBefore(thirtyDaysAgo, 'day') || current.isAfter(today, 'day');
    };

    const handleReset = () => {
        onFiltersChange({
            ...filters,
            taskDate: dayjs(),
            selectedDsps: [],
            selectedAreas: [],
            selectedDrivers: [],
            selectedCities: []
        });
    };
    
    return (
        <div className="mb-2 pl-0 py-2">
            <div className="flex flex-col lg:flex-row gap-4">
                <div className="flex-1">
                    {/* 使用 Grid 布局确保列对齐 */}
                    <div className="grid grid-cols-3 gap-x-10 gap-y-4 items-start">
                        {/* 任务日期 */}
                        <div className="min-w-0">
                            <DateFilterItem
                                label={labelTaskDate}
                                value={filters.taskDate}
                                onChange={(date) => onFiltersChange({...filters, taskDate: date || dayjs()})}
                                disabledDate={disabledDate}
                                spanClassName={getSmartLabelClassName(col1Labels)}
                            />
                        </div>
                                {/* DSP 下拉：DSP 用户禁用（数据恒按其自身 DSP 过滤，可选也无效），与抽屉筛选 TaskDetailFilter 口径一致 */}
                                <div className="min-w-0">
                                    <ParcelFilterStyle
                                        label={labelDsp}
                                        options={dsps.map(dsp => ({ value: dsp.name, label: dsp.name }))}
                                        value={filters.selectedDsps}
                                        onChange={(value: string[]) => {
                                            onFiltersChange({...filters, selectedDsps: value});
                                        }}
                                        className="w-full"
                                        spanClassName={getSmartLabelClassName(col2Labels)}
                                        mode="multiple"
                                        showSearch
                                        onSearch={withSearchDebounce('dsp', onDspSearch)}
                                        onDropdownVisibleChange={withKeywordResetOnClose('dsp', onDspSearch)}
                                        filterOption={onDspSearch ? false : undefined}
                                        loading={dspOptionsLoading}
                                        disabled={dspMode}
                                        allowClear={!dspMode}
                                        placeholder={t('wms.delivery.dailyTaskMonitoring.buttons.dsp') || undefined}
                                    />
                                </div>
                                {/* 司机维度：DSP + 司机；区域维度：DSP + 区域 */}
                        {filters.viewMode === 'area' && (
                                    <div className="min-w-0">
                                        <ParcelFilterStyle
                                            label={labelArea}
                                            options={areas.map(area => ({ value: area.name, label: area.name }))}
                                            value={filters.selectedAreas}
                                            onChange={(value: string[]) => {
                                                onFiltersChange({...filters, selectedAreas: value});
                                            }}
                                            className="w-full"
                                            spanClassName={getSmartLabelClassName(col3Labels)}
                                            mode="multiple"
                                            showSearch
                                            onSearch={withSearchDebounce('area', onAreaSearch)}
                                            onDropdownVisibleChange={withKeywordResetOnClose('area', onAreaSearch)}
                                            filterOption={onAreaSearch ? false : undefined}
                                            loading={areaOptionsLoading}
                                            placeholder={t('wms.delivery.dailyTaskMonitoring.buttons.area') || undefined}
                                        />
                                    </div>
                                )}
                        {filters.viewMode === 'driver' && (
                                    <div className="min-w-0">
                                        <ParcelFilterStyle
                                            label={labelDriver}
                                            options={drivers.map(driver => ({ value: String(driver.accountId), label: driver.name, title: driver.title }))}
                                            value={filters.selectedDrivers}
                                            onChange={(value: string[]) => {
                                                onFiltersChange({...filters, selectedDrivers: value});
                                            }}
                                            className="w-full"
                                            spanClassName={getSmartLabelClassName(col3Labels)}
                                            mode="multiple"
                                            showSearch
                                            onSearch={withSearchDebounce('driver', onDriverSearch)}
                                            onDropdownVisibleChange={withKeywordResetOnClose('driver', onDriverSearch)}
                                            filterOption={onDriverSearch ? false : undefined}
                                            loading={driverOptionsLoading}
                                            placeholder={t('wms.delivery.dailyTaskMonitoring.buttons.driver') || undefined}
                                        />
                                    </div>
                        )}
                    </div>
                    {/* 按钮区域 */}
                    {showResetButton && (
                        <div className="mb-1 flex justify-end gap-4 pl-0">
                            <Button type="default" onClick={handleReset} className="w-auto min-w-24">
                                <span className="font-bold">{t('components.button.reset')}</span>
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TaskMonitoringFilter;
